/**
 * Кабинет заказчика.
 *
 * Один постоянный адрес вместо десятка разовых ссылок. Колонка — один
 * заказ, он же один канал; заказчик подписывает колонки сам, потому что
 * «ТЗ #14» ему ничего не говорит.
 *
 * Что видно: готовые работы со скачиванием, процент готовности по тем,
 * что ещё в работе, и галочка «выложил». Ни цен, ни исполнителей,
 * ни сроков — заказчику это не нужно, а нам не надо объясняться.
 */
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api';
import { bytes, duration } from '../lib/format';

type PortalVideo = {
  id: string;
  index: number;
  marked: boolean;
  durationSec: number | null;
  final: { id: string; name: string; sizeBytes: string } | null;
  cover: { id: string; name: string } | null;
  previewUrl: string | null;
};

type PortalColumn = {
  id: string;
  label: string;
  total: number;
  done: number;
  marked: number;
  percent: number;
  videos: PortalVideo[];
};

type PortalContent = { title: string; columns: PortalColumn[] };

export function PortalPage() {
  const { token } = useParams<{ token: string }>();
  const [params, setParams] = useSearchParams();
  const password = params.get('p') ?? '';
  const [passInput, setPassInput] = useState('');
  const qc = useQueryClient();

  const key = ['portal', token, password];
  const auth = password ? `?password=${encodeURIComponent(password)}` : '';

  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => api.get<PortalContent>(`/api/portal/${token}${auth}`),
    enabled: Boolean(token),
    retry: false,
  });

  const rename = useMutation({
    mutationFn: (v: { columnId: string; label: string }) =>
      api.patch(`/api/portal/${token}/columns/${v.columnId}`, { label: v.label, password }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  const mark = useMutation({
    mutationFn: (v: { videoId: string; marked: boolean }) =>
      api.post(`/api/portal/${token}/videos/${v.videoId}/mark`, {
        marked: v.marked,
        password,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  const download = async (fileId: string) => {
    const res = await api.get<{ url: string }>(`/api/portal/${token}/file/${fileId}${auth}`);
    window.location.href = res.url;
  };

  // Нужен пароль — показываем поле вместо ошибки
  if (error instanceof ApiError && error.status === 403) {
    return (
      <Shell>
        <div className="card max-w-sm mx-auto">
          <div className="font-bold mb-1">Кабинет защищён паролем</div>
          <div className="text-[12.5px] text-muted2 mb-3">
            Введи пароль, который тебе передали.
          </div>
          <input
            className="input mb-2"
            type="password"
            value={passInput}
            onChange={(e) => setPassInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setParams({ p: passInput })}
            autoFocus
          />
          <button className="btn btn-primary w-full" onClick={() => setParams({ p: passInput })}>
            Войти
          </button>
        </div>
      </Shell>
    );
  }

  if (error instanceof ApiError && error.status === 429) {
    return (
      <Shell>
        <div className="text-center py-16">
          <div className="text-4xl opacity-30 mb-4">◔</div>
          <div className="text-lg font-bold mb-2">Слишком много запросов</div>
          <div className="text-[13px] text-muted2 max-w-xs mx-auto mb-4 leading-relaxed">
            С кабинетом всё в порядке. Подожди минуту и обнови страницу.
          </div>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Обновить
          </button>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="text-center py-16">
          <div className="text-4xl opacity-30 mb-4">◎</div>
          <div className="text-lg font-bold mb-2">Кабинет недоступен</div>
          <div className="text-[13px] text-muted2 max-w-xs mx-auto leading-relaxed">
            Проверь ссылку или попроси новую у того, кто её прислал.
          </div>
        </div>
      </Shell>
    );
  }

  if (isLoading || !data) {
    return (
      <Shell>
        <div className="flex gap-4 animate-pulse">
          {[0, 1, 2].map((i) => (
            <div key={i} className="w-[280px] h-72 rounded-xl2 bg-surface border border-line" />
          ))}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold tracking-[-0.02em]">{data.title}</h1>
        <div className="text-[13px] text-muted2 mt-1.5">
          Каналов: {data.columns.length} · нажми на название колонки, чтобы переименовать
        </div>
      </div>

      {data.columns.length === 0 ? (
        <div className="card text-center py-12 text-muted2">
          Пока пусто. Заказы появятся здесь, как только их добавят.
        </div>
      ) : (
        /* Колонки в ряд с прокруткой вбок — на телефоне листается пальцем */
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-5 px-5">
          {data.columns.map((c) => (
            <Column
              key={c.id}
              column={c}
              onRename={(label) => rename.mutate({ columnId: c.id, label })}
              onMark={(videoId, marked) => mark.mutate({ videoId, marked })}
              onDownload={download}
            />
          ))}
        </div>
      )}
    </Shell>
  );
}

function Column({
  column,
  onRename,
  onMark,
  onDownload,
}: {
  column: PortalColumn;
  onRename: (label: string) => void;
  onMark: (videoId: string, marked: boolean) => void;
  onDownload: (fileId: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(column.label);

  const inProgress = column.done < column.total;

  return (
    <div className="w-[290px] shrink-0">
      {/* Заголовок колонки — он же название канала у заказчика */}
      {editing ? (
        <input
          className="input mb-2 font-bold"
          value={draft}
          maxLength={60}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            setEditing(false);
            if (draft.trim() && draft !== column.label) onRename(draft.trim());
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setDraft(column.label);
              setEditing(false);
            }
          }}
        />
      ) : (
        <button
          className="w-full text-left font-bold text-[15px] mb-2 px-1 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors truncate"
          onClick={() => {
            setDraft(column.label);
            setEditing(true);
          }}
          title="Нажми, чтобы переименовать"
        >
          {column.label}
        </button>
      )}

      {/* Готовность. Заказчику важен процент, а не внутренние статусы */}
      <div className="card mb-3 py-3">
        <div className="flex items-baseline gap-2">
          <span className="mono text-xl font-extrabold">{column.percent}%</span>
          <span className="text-[12px] text-muted2">
            готово {column.done} из {column.total}
          </span>
        </div>
        <div className="h-1.5 bg-bg rounded-full mt-2 overflow-hidden">
          <div
            className="h-full bg-accent rounded-full transition-all"
            style={{ width: `${column.percent}%` }}
          />
        </div>
        {inProgress && (
          <div className="text-[11.5px] text-muted2 mt-2">
            Остальное ещё в работе, появится здесь по мере готовности.
          </div>
        )}
        {column.marked > 0 && (
          <div className="text-[11.5px] text-ok mt-1">выложено: {column.marked}</div>
        )}
      </div>

      <div className="space-y-2.5">
        {column.videos.length === 0 ? (
          <div className="card text-center py-8 text-[12.5px] text-muted2">
            Готовых работ пока нет
          </div>
        ) : (
          column.videos.map((v) => (
            <div key={v.id} className={`card p-3 ${v.marked ? 'opacity-60' : ''}`}>
              {v.previewUrl && (
                <img
                  src={v.previewUrl}
                  alt=""
                  loading="lazy"
                  className="w-full aspect-video object-cover rounded-lg border border-line mb-2.5"
                />
              )}

              <div className="flex items-center gap-2 mb-2">
                <span className="font-bold text-[13.5px]">Видео {v.index}</span>
                <span className="text-[11.5px] text-muted2">
                  {v.durationSec ? duration(v.durationSec) : ''}
                  {v.final ? ` · ${bytes(v.final.sizeBytes)}` : ''}
                </span>
              </div>

              <div className="flex gap-2 mb-2.5">
                {v.final && (
                  <button
                    className="btn btn-sm btn-primary flex-1"
                    onClick={() => onDownload(v.final!.id)}
                  >
                    ⤓ Видео
                  </button>
                )}
                {v.cover && (
                  <button className="btn btn-sm" onClick={() => onDownload(v.cover!.id)}>
                    ⤓ Обложка
                  </button>
                )}
              </div>

              {/* Личная отметка заказчика: что уже ушло на канал */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={v.marked}
                  onChange={(e) => onMark(v.id, e.target.checked)}
                />
                <span className={`text-[12.5px] ${v.marked ? 'text-ok' : 'text-muted2'}`}>
                  {v.marked ? 'Выложено' : 'Отметить как выложенное'}
                </span>
              </label>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-[1400px] mx-auto px-5 py-10">
        <div className="text-[24px] tracking-[0.01em] font-extrabold text-accent2 mb-8">
          Конторка
        </div>
        {children}
      </div>
    </div>
  );
}
